import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaNewspaper, FaCalendar, FaClock, FaArrowRight } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import config from '../config';

const Insights = ({ market }) => {
  const b = getBrand(market);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const apiUrl = `${config.apiUrl}/articles`;
    fetch(apiUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setArticles(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading articles:', err);
        setLoading(false);
      });
  }, []);

  const categories = [
    { id: 'all',        name: 'All Articles' },
    { id: 'technology', name: 'Technology' },
    { id: 'business',   name: 'Business' },
    { id: 'security',   name: 'Security' },
    { id: 'cloud',      name: 'Cloud' },
    { id: 'ai',         name: 'AI & Automation' }
  ];

  const filteredArticles = filter === 'all'
    ? articles
    : articles.filter(a => a.category === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateReadTime = (content) => {
    return Math.ceil(content.split(' ').length / 200);
  };

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title="Insights & Articles"
        subtitle={market === 'lu'
          ? 'Expert insights on IT, cloud, security, and digital transformation for Luxembourg businesses.'
          : 'Latest insights, best practices, and thought leadership in enterprise IT and digital transformation.'}
        icon={<FaNewspaper color="#1A237E" />}
      />

      {/* ── CATEGORY FILTER ── */}
      <section style={{ background: b.bgAlt, padding: '32px clamp(24px, 6vw, 80px)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
          {categories.map((cat) => {
            const active = filter === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                style={{
                  padding: b.isMWO ? '8px 20px' : '8px 22px',
                  border: active ? `2px solid ${b.accent}` : `2px solid ${b.isMWO ? 'rgba(232,228,220,0.15)' : '#e2e8f0'}`,
                  background: active ? (b.isMWO ? 'rgba(0,188,212,0.12)' : '#EFF6FF') : 'transparent',
                  color: active ? b.accent : b.textMuted,
                  borderRadius: b.isMWO ? '0' : '24px',
                  cursor: 'pointer',
                  fontFamily: b.isMWO ? "'Barlow Condensed', sans-serif" : b.fontBody,
                  fontWeight: active ? 700 : 500,
                  fontSize: b.isMWO ? '0.85rem' : '0.88rem',
                  letterSpacing: b.isMWO ? '0.06em' : 'normal',
                  textTransform: b.isMWO ? 'uppercase' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* ── ARTICLES GRID ── */}
      <section style={{ background: b.bgAlt, padding: `0 clamp(24px, 6vw, 80px) clamp(60px, 8vw, 96px)`, minHeight: '400px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p style={{ color: b.textMuted, fontSize: b.fz.lg }}>Loading articles…</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <FaNewspaper size={48} color={b.isMWO ? 'rgba(232,228,220,0.2)' : '#CBD5E1'} style={{ marginBottom: '20px' }} />
              <p style={{ color: b.textMuted, fontSize: b.fz.lg, marginBottom: '8px' }}>No articles found in this category.</p>
              <p style={{ color: b.textMuted, fontSize: b.fz.sm, opacity: 0.7 }}>Check back soon for new insights!</p>
            </div>
          ) : (
            <div className="grid-3" style={{ paddingTop: '48px' }}>
              {filteredArticles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                  style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, overflow: 'hidden' }}
                >
                  <Link to={`/insights/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    {/* Image */}
                    {article.image && (
                      <div style={{ height: '190px', overflow: 'hidden', background: b.bgAlt }}>
                        <img src={article.image} alt={article.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}

                    <div style={{ padding: '24px' }}>
                      {/* Category badge */}
                      <div style={{
                        display: 'inline-block',
                        background: b.isMWO ? 'rgba(0,188,212,0.12)' : '#EFF6FF',
                        color: b.accent,
                        padding: '4px 12px',
                        borderRadius: b.isMWO ? '0' : '12px',
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        marginBottom: '14px'
                      }}>
                        {article.category}
                      </div>

                      {/* Title */}
                      <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px', lineHeight: 1.35 }}>
                        {article.title}
                      </h3>

                      {/* Excerpt */}
                      <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '16px' }}>
                        {article.excerpt}
                      </p>

                      {/* Meta */}
                      <div style={{
                        display: 'flex', gap: '16px', fontSize: b.fz.xs, color: b.isMWO ? 'rgba(232,228,220,0.35)' : '#94a3b8',
                        borderTop: `1px solid ${b.isMWO ? 'rgba(232,228,220,0.1)' : '#f1f5f9'}`,
                        paddingTop: '14px', marginBottom: '12px'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <FaCalendar size={10} /> {formatDate(article.publishedDate)}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <FaClock size={10} /> {calculateReadTime(article.content)} min read
                        </span>
                      </div>

                      {/* Read more */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: b.accent, fontWeight: 600, fontSize: b.fz.xs }}>
                        Read More <FaArrowRight size={11} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
